import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Truck, CheckCircle, MapPin, Clock } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const STEP_ICONS = {
  "Guía generada": Package,
  "Recolección programada": Clock,
  "En tránsito": Truck,
  "En reparto": MapPin,
  "Entregado": CheckCircle,
};

const TrackingTimeline = ({ shipmentId }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const res = await axios.get(`${API}/shipments/${shipmentId}/tracking`);
        setTracking(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    if (shipmentId) fetchTracking();
  }, [shipmentId]);

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!tracking) return <p className="text-sm text-zinc-500 text-center py-4">No se encontró información de rastreo</p>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-violet-600" />
            Rastreo del envío
          </CardTitle>
          <Badge variant="outline" className="font-mono">{tracking.tracking_number}</Badge>
        </div>
        <p className="text-sm text-zinc-500">Paquetería: {tracking.carrier}</p>
      </CardHeader>
      <CardContent>
        <div className="relative ml-4">
          {tracking.timeline.map((step, i) => {
            const Icon = STEP_ICONS[step.status] || Package;
            const isLast = i === tracking.timeline.length - 1;
            return (
              <div key={i} className="flex gap-4 pb-6 last:pb-0" data-testid={`tracking-step-${i}`}>
                {/* Line + dot */}
                <div className="flex flex-col items-center relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                    step.completed ? "bg-violet-600 text-white" : "bg-zinc-200 text-zinc-400"
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {!isLast && (
                    <div className={`w-0.5 flex-1 mt-1 ${step.completed ? "bg-violet-600" : "bg-zinc-200"}`} />
                  )}
                </div>
                {/* Content */}
                <div className="pt-1.5 flex-1">
                  <p className={`font-semibold text-sm ${step.completed ? "text-zinc-900" : "text-zinc-400"}`}>
                    {step.status}
                  </p>
                  <p className={`text-sm ${step.completed ? "text-zinc-500" : "text-zinc-300"}`}>
                    {step.description}
                  </p>
                  {step.date && step.completed && (
                    <p className="text-xs text-violet-500 mt-0.5">
                      {new Date(step.date).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default TrackingTimeline;
